import {
  CardMeta,
  CardMetaGrid,
  ManagementCard,
  ManagementCardList,
  Pill,
} from 'components/ManagementCard'
import {
  banStatusStrings,
  commonStrings,
  organizationManagementStrings,
} from 'constants/strings'
import type { TBanStatusOrganization } from 'types'
import { formatNumber } from 'utils'
import {
  formatBanStatusCount,
  getCategoryLabel,
  isOrganizationBlocked,
  ORGANIZATION_USER_COUNT_KEYS,
} from './banStatusView'
import { BanStatusLabel } from './BanStatusLabel'

type OrganizationBreakdownCardListProps = {
  organizations: TBanStatusOrganization[]
  loading: boolean
  emptyMessage?: string
  className?: string
}

const headers = banStatusStrings.organizations.headers

export function OrganizationBreakdownCardList({
  organizations,
  loading,
  emptyMessage = banStatusStrings.organizations.empty,
  className,
}: OrganizationBreakdownCardListProps) {
  return (
    <ManagementCardList
      loading={loading}
      isEmpty={organizations.length === 0}
      loadingMessage={commonStrings.loading}
      emptyMessage={emptyMessage}
      className={className}
    >
      {organizations.map((organization) => (
        <li key={organization.organizationId}>
          <ManagementCard
            title={organization.organizationName}
            badges={
              <>
                {isOrganizationBlocked(organization) && (
                  <Pill tone="danger">{headers.allocatable}0</Pill>
                )}
                <Pill tone="neutral" className="ml-auto">
                  {organizationManagementStrings.planLabels[organization.plan]}
                </Pill>
              </>
            }
          >
            <CardMetaGrid cols={3}>
              <CardMeta
                label={<BanStatusLabel label={headers.total} help="total" />}
                value={formatNumber(organization.total)}
              />
              <CardMeta
                label={<BanStatusLabel label={headers.allocatable} help="allocatable" />}
                value={formatNumber(organization.allocatable)}
              />
              <CardMeta
                label={<BanStatusLabel label={getCategoryLabel('autoPaused')} help="autoPaused" />}
                value={formatNumber(organization.autoPaused)}
              />
              <CardMeta
                label={<BanStatusLabel label={getCategoryLabel('manualPaused')} help="manualPaused" />}
                value={formatNumber(organization.manualPaused)}
              />
              <CardMeta
                label={<BanStatusLabel label={getCategoryLabel('unlinked')} help="unlinked" />}
                value={formatNumber(organization.unlinked)}
              />
              <CardMeta
                label={<BanStatusLabel label={getCategoryLabel('notAllocatable')} help="notAllocatable" />}
                value={formatNumber(organization.notAllocatable)}
              />
              <CardMeta
                label={<BanStatusLabel label={getCategoryLabel('webhookInvalid')} help="webhookInvalid" />}
                value={formatNumber(organization.webhookInvalid)}
              />
            </CardMetaGrid>
            <div className="mt-3 border-t border-border/60 pt-3">
              <h3 className="mb-2 text-xs font-semibold text-ink">
                {banStatusStrings.organizations.userCountsHeading}
              </h3>
              <dl className="grid grid-cols-2 gap-2 [&>div:last-child]:col-span-2">
                {ORGANIZATION_USER_COUNT_KEYS.map((key) => (
                  <CardMeta
                    key={key}
                    testId={`org-card-metric-${key}`}
                    label={<BanStatusLabel label={headers[key]} help={key} />}
                    value={formatBanStatusCount(organization[key])}
                  />
                ))}
              </dl>
            </div>
          </ManagementCard>
        </li>
      ))}
    </ManagementCardList>
  )
}
